import { useRef } from 'react';
import { ModalOverlay } from '../components/ModalOverlay';
import { useModalA11y } from '../components/useModalA11y';
import { ArrowPathIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * Modale de confirmation LÉGÈRE et RÉUTILISABLE (T5 lot B — suppression / fusion de
 * tags). Vraie boîte de dialogue (`role="dialog"` + `aria-modal`) : focus initial sur
 * le bouton de confirmation, restauration du focus, verrou de défilement, Échap ferme
 * (sauf pendant un appel réseau). Le caller l'enveloppe dans un `<Portal>` (l'en-tête
 * porte `backdrop-blur`).
 *
 * L'action de confirmation est neutre visuellement par défaut ; `danger` la teinte en
 * `bg-danger` (l'INFORMATION reste portée par le TITRE/le texte, jamais la couleur seule).
 */
interface ConfirmModalProps {
  title: string;
  /** Contenu explicatif (déjà traduit/interpolé). */
  children: React.ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  busyLabel: string;
  busy: boolean;
  danger?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmModal({
  title,
  children,
  confirmLabel,
  cancelLabel,
  busyLabel,
  busy,
  danger = false,
  onConfirm,
  onClose,
}: ConfirmModalProps): React.JSX.Element {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y({ dialogRef, initialFocusRef: confirmRef, onEscape: !busy ? onClose : undefined });

  const confirmClass = danger
    ? 'bg-danger text-danger-fg focus-visible:outline-danger'
    : 'bg-brand text-brand-fg focus-visible:outline-brand';

  return (
    <ModalOverlay onDismiss={busy ? undefined : onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-lg border border-border bg-surface text-fg shadow-xl"
      >
        <div className="flex items-center justify-between gap-2 border-b border-border p-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-fg">
            <ExclamationTriangleIcon
              className={danger ? 'h-6 w-6 text-danger' : 'h-6 w-6 text-muted'}
              aria-hidden="true"
            />
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            title={cancelLabel}
            aria-label={cancelLabel}
            className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <div className="flex min-h-0 flex-col gap-3 overflow-y-auto p-4 text-sm text-fg">
          {children}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border p-4">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${confirmClass}`}
          >
            {busy && <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" />}
            {busy ? busyLabel : confirmLabel}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
